/*
 * @providesModule EmailSignInView
 */

import React, {useState, useEffect, useRef} from 'react';
import {
  KeyboardAvoidingView,
  Modal,
  Platform,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import {Trans} from 'react-i18next';
import {_m} from 'INTL';
import {BannerAnimated, SkillzTextInput, ToastViewAnimated} from '@skillz/ui';
import ForgotPasswordView from 'ForgotPasswordView';
import ForgotPasswordForm from 'ForgotPasswordForm';
import {getStyles} from 'EmailSignInStyle';
import WebView from 'react-native-webview';
import {trackTelemetryEvent} from 'TrackTelemetryEvent';
import {TelemetryEventName} from 'TelemetryConstants';
import PushButton from 'PushButton';
import SkillzStyle from 'SkillzStyle';
import {EmailCodeInput} from 'EmailCodeInput';
import {EmailFieldType} from '../SignInView';
import CurrentUserStore from 'CurrentUserStore';
import {SKZUserInterface} from 'SKZUser';
import Tournament from 'SKZTournament';
import NavigationManager from 'NavigationManager';
import SkillzSDK from 'SkillzSDK';

export const TERM_OF_SERVICE_URL = 'https://skillz.com/legal';

export interface EmailSignInProps {
  //Handle callback on "Sign In" button
  onSignInPress: (username: string, password: string) => void;
  //Handle Forgot Password Action
  forgotPasswordAction?: (username: string) => Promise<boolean>;
  /** Object that passes state and state setting function to show/hide Tos */
  tosValues?: {
    showTos: boolean;
    setShowTos: React.Dispatch<React.SetStateAction<boolean>>;
  };
  /** Loading functions show/hide*/
  loading?: {
    show: () => void;
    hide: () => void;
  };
  //User's email to pre-populate the field
  email?: string;
  colorVariant?: 'dark';
  isNewUI?: boolean;
  gameTheme?: any;
  //Callback to notify parent when email verification state changes
  onEmailVerificationStateChange?: (isActive: boolean) => void;

  fieldType?: EmailFieldType;

  time?: number;

  setEmailUsername?: (value: string) => void;

  handlePassword?: (value: string) => void;

  header?: {title: string; description: string};

  // to render placeholder accordingly
  isSignUp?: boolean;

  isEmailVerificationActive?: boolean;

  // whether to show OTP option for the input text (email/username)
  setIsValidEmail: (isEmailValid: boolean) => void;

  onSignInWithEmailOTP: (
    email: string,
    token: string,
    isSignUp: boolean,
    onDone: ({data, success}: {data: any; success: boolean}) => void
  ) => void;

  // On sign up with email password
  onSignUpPress?: (
    email: string,
    password: string,
    onDone?: (success: boolean) => void,
    tournament?: Tournament,
    navigator?: Navigator
  ) => any;

  onOtpMethodSelected: () => Promise<boolean>;

  // Callback to pass errors back to parent component
  onError?: (error: string) => void;

  // Callback for code validation to trigger exit deposit flow
  codeValidation?: () => void;
}

const TOAST_FADE_OUT_DURATION = 3000;
const TEST_IDS = {
  modalTestID: 'sign_in_modal',
  inputUsernameTestID: 'sign_in_username_input',
  inputPasswordTestID: 'sign_in_password_input',
  signInTestID: 'sign_in_button',
  forgotPasswordTestID: 'sign_in_forgot_password_botton',
  serviceTermMessage: 'sign_in_service_terms_message',
  serviceTermButton: 'sign_in_service_terms_button',
};

const EmailSignInView = (props: EmailSignInProps) => {
  const [password, setPassword] = useState<string>('');
  const [email, setEmail] = useState<string>(props.email ?? '');
  //Forgot Password Modal
  const [modalVisible, setModalVisible] = useState(false);
  //Password Reset Banner Visible
  const [resetBannerVisible, setResetBannerVisible] = useState(false);
  //Password Reset Email
  const [passwordResetEmail, setPasswordResetEmail] = useState<string>();
  //Password Reset Toast Visible
  const [toastVisible, setToastVisible] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState<string>('');
  const [inputUserNameFocus, setUserNameFocus] = useState<boolean>(false);
  const [inputError, setInputError] = useState<boolean>(false);
  const styles = getStyles(props.colorVariant);
  const focusColor = props.colorVariant === 'dark' ? '#fff' : '#4890E8';
  const placeholderTextColor = props.colorVariant === 'dark' ? '#fff' : undefined;

  const {statusBarContentStyle} = SkillzStyle.fullStyles;
  const isDarkTheme = statusBarContentStyle === 0;

  const [isVerifiedEmail, setIsVerifiedEmail] = useState(false);
  const [otpError, setOtpError] = useState<string>('');

  const inputRef = useRef<TextInput>(null);
  const theme =
    SkillzStyle.getOverriddenFallbackTheme();

  const st = createStyles(props.gameTheme || theme, isDarkTheme);

  const {textColorSecondary, playButtonGradient, textColorPrimary} = theme;

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 300); // slight delay ensures keyboard opens reliably
    return () => clearTimeout(timer);
  }, []);

  // Notify parent component when email verification state changes
  useEffect(() => {
    props.onEmailVerificationStateChange?.(isVerifiedEmail);
  }, [isVerifiedEmail, props.onEmailVerificationStateChange]);

  useEffect(() => {
    fetchUserInfo();
  }, []);

  const renderUsernameTextField = () => {
    return (
      <View style={styles.inputView}>
        <SkillzTextInput
          autoCorrect={false}
          focusColor={focusColor}
          placeholderTextColor={placeholderTextColor}
          autoCapitalize={'none'}
          testID={TEST_IDS.inputUsernameTestID}
          label={_m('Username or email')}
          value={email}
          onChangeText={(value) => setEmail(value)}
          inputStyle={styles.textInInput}
          containerStyle={styles.inputContainer}
          styleLabel={styles.inputLabel}
          keyboardType={'email-address'}
        />
      </View>
    );
  };

  const renderPasswordTextField = () => {
    return (
      <View style={styles.inputView}>
        <SkillzTextInput
          autoCorrect={false}
          focusColor={focusColor}
          placeholderTextColor={placeholderTextColor}
          autoCapitalize={'none'}
          testID={TEST_IDS.inputPasswordTestID}
          label={_m('Password')}
          value={password}
          onChangeText={(value) => setPassword(value)}
          secureTextEntry
          containerStyle={styles.inputContainer}
          styleLabel={styles.inputLabel}
          inputStyle={styles.textInInput}
          iconStyle={styles.passwordIcon}
          keyboardType={'default'}
          showIcon={true}
        />
      </View>
    );
  };

  const handleSignInPress = () => {
    if (email.length) {
      trackTelemetryEvent({
        event: TelemetryEventName.SIGN_IN_CTA_CLICK,
        payload: {cta: 'sign in'},
      });
      props.onSignInPress(email, password);
    }
  };

  const [userInfo, setUserInfo] = useState<SKZUserInterface | null>(null);

  const fetchUserInfo = async () => {
    await CurrentUserStore.fetchUser()
      .then((userInfo: SKZUserInterface) => {
        setUserInfo(userInfo);
      })
      .catch((error: Error) => {
        console.error('getFtueCredentials: Cannot fetch user info:' + JSON.stringify(error));
      });
  };

  const handleOtpVerification = async (code: string) => {
    try {
      setOtpError('');
      props.onSignInWithEmailOTP(email, code, !!props?.isSignUp, ({data, success}) => {
        if (!success) {
          // Pass error back to parent component instead of handling locally
          const errorMessage = (data as any)?.message || 'Verification failed';
          props.onError?.(errorMessage);
        } else {
          props.codeValidation?.();
          SkillzSDK.resetToHomeScreen();
        }
      });
    } catch (error) {
      console.error('OTP verification failed:', error);
      // Pass error back to parent component instead of handling locally
      props.onError?.(error instanceof Error ? error.message : 'An error occurred');
    }
  };

  const renderContainerView = () => {
    return (
      <View style={styles.skillzLoginSection}>
        <Text allowFontScaling={false} style={styles.headerText}>
          {_m('Welcome back! Sign in with your username or email.')}
        </Text>
        {renderUsernameTextField()}
        {renderPasswordTextField()}
        {renderTermsOfServiceText()}
        <TouchableOpacity
          onPress={handleSignInPress}
          testID={TEST_IDS.signInTestID}
          style={styles.loginBtn}
        >
          <Text allowFontScaling={false} style={styles.loginText}>
            {_m('Sign In')}
          </Text>
        </TouchableOpacity>
        <></>
      </View>
    );
  };

  const renderTermsOfServiceText = () => {
    return (
      <View style={styles.termsOfServiceText} testID={TEST_IDS.serviceTermMessage}>
        <Trans
          t={_m}
          i18nKey={'AUTH_TOS_PP_CONSENT_LINKS'}
          parent={Text}
          values={{button_label: _m('Sign In')}}
          components={{
            link1: (
              <Text
                allowFontScaling={false}
                testID={TEST_IDS.serviceTermButton}
                onPress={() => {
                  trackTelemetryEvent({
                    event: TelemetryEventName.SIGN_IN_CTA_CLICK,
                    payload: {cta: 'terms of use'},
                  });
                  if (Platform.OS === 'web') {
                    window.open(TERM_OF_SERVICE_URL, '_blank');
                  } else {
                    props.loading?.show();
                    props.tosValues?.setShowTos(true);
                  }
                }}
                suppressHighlighting={true}
                style={styles.hyperlinkStyle}
              />
            ),
          }}
          style={styles.footerTextStyle}
        />
      </View>
    );
  };

  const renderTOS = () => {
    return props.tosValues?.showTos ? (
      <SafeAreaView style={styles.tosView}>
        <WebView source={{uri: TERM_OF_SERVICE_URL}} onLoad={props.loading?.hide} />
      </SafeAreaView>
    ) : null;
  };

  const renderPasswordResetSuccessToast = () => {
    if (toastVisible) {
      setTimeout(() => {
        setToastVisible(false);
      }, TOAST_FADE_OUT_DURATION);
      return (
        <View style={styles.toastContainer}>
          <ToastViewAnimated text={_m('Reset password email sent!')} />
        </View>
      );
    }
    return false;
  };

  const renderPasswordResetSuccessBanner = () => {
    if (resetBannerVisible) {
      return (
        <BannerAnimated
          title={_m('Password Reset')}
          message={_m('Instructions were sent to your email.')}
          buttonTitle={_m('Resend email')}
          onClosePress={() => setResetBannerVisible(false)}
          onActionPress={handleResendEmailPress}
        />
      );
    }
    return false;
  };

  const handleForgotPasswordCallback = (email: string) => {
    setModalVisible(!modalVisible);
    setResetBannerVisible(true);
    setPasswordResetEmail(email);
    trackTelemetryEvent({
      event: TelemetryEventName.SIGN_IN_CTA_CLICK,
      payload: {cta: 'forgot your password'},
    });
  };

  const handleWebFormChangeText = (text: string) => {
    setInputError(false);
    setForgotPasswordEmail(text);
  };

  const handleWebFormSubmit = async () => {
    if (forgotPasswordEmail.length && props.forgotPasswordAction) {
      try {
        const success = await props.forgotPasswordAction(forgotPasswordEmail);
        if (!success) {
          setInputError(true);
        } else {
          setModalVisible(false);
          setResetBannerVisible(true);
          setPasswordResetEmail(forgotPasswordEmail);
        }
      } catch (error) {
        console.warn('Error in submit press', error);
      }
    }
  };

  const handleWebFormCancel = () => {
    setModalVisible(false);
    setForgotPasswordEmail('');
    setInputError(false);
    setUserNameFocus(false);
  };

  const handleResendEmailPress = async () => {
    if (passwordResetEmail != null && props.forgotPasswordAction) {
      try {
        await props.forgotPasswordAction(passwordResetEmail);
        setToastVisible(true);
      } catch (error) {
        console.warn('Error in submit press', error);
      }
    }
  };

  const renderForgotPasswordModal = () => {
    if (Platform.OS === 'web') {
      return (
        <Modal
          animationType='fade'
          transparent={false}
          visible={modalVisible}
          onDismiss={handleWebFormCancel}
        >
          <SafeAreaView style={[styles.container, st.modalContainer]}>
            <ForgotPasswordForm
              email={forgotPasswordEmail}
              onChangeText={handleWebFormChangeText}
              onSubmitPress={handleWebFormSubmit}
              onCancelPress={handleWebFormCancel}
              inputUserNameFocus={inputUserNameFocus}
              setUserNameFocus={setUserNameFocus}
              inputError={inputError}
              viewHeight={400}
            />
          </SafeAreaView>
        </Modal>
      );
    } else {
      return (
        <Modal animationType='fade' transparent={true} visible={modalVisible}>
          <ForgotPasswordView
            onCancelPress={() => {
              setModalVisible(!modalVisible);
            }}
            onSubmitPress={props.forgotPasswordAction}
            submitResponseCallback={handleForgotPasswordCallback}
          />
        </Modal>
      );
    }
  };

  const renderNewUINextButton = () => {
    // Email validation regex
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    // Username validation regex - allows letters, numbers, and special characters without whitespace
    const usernameRegex = /^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+$/;
    const trimmedInput = email.trim();
    const isValidEmail = trimmedInput && emailRegex.test(trimmedInput);
    const isValidUsername = trimmedInput && usernameRegex.test(trimmedInput);
    const isValid = props?.isSignUp ? isValidEmail : isValidEmail || isValidUsername;

    const handleNextPress = async () => {
      // Validate input (email or username) before proceeding

      if (!trimmedInput) {
        setInputError(true);
        return;
      }
      if (!isValidEmail && !isValidUsername) {
        setInputError(true);
        return;
      }
      // Clear any existing errors and proceed
      setInputError(false);
      setEmail(trimmedInput);

      // Send email/username OTP and wait for response
      if (isValidEmail) {
        props.setIsValidEmail(true);

        try {
          // Call handleEmailOtp and wait for response
          const otpSuccess = await props.onOtpMethodSelected();

          if (otpSuccess) {
            // Only switch to EmailCodeInput if OTP sending succeeds
            setIsVerifiedEmail(true);
            props.onEmailVerificationStateChange?.(true);
          }
          // If OTP sending fails, error will be handled by the callback in SignInView
        } catch (error) {
          console.error('Error sending OTP:', error);
          props.onError?.(error instanceof Error ? error.message : 'Failed to send OTP');
        }
      } else {
        // For non-email inputs (usernames), proceed directly
        setIsVerifiedEmail(true);
        props.onEmailVerificationStateChange?.(true);
      }
    };

    return (
      <View style={st.nextButtonContainer}>
        <PushButton
          colors={playButtonGradient}
          title='Next'
          onPress={handleNextPress}
          cornerRadius={12}
          textStyle={st.buttonTextStyle}
          style={{height: 56, opacity: !isValid ? 0.6 : 1}}
          disabled={!isValid}
        />
      </View>
    );
  };

  if (props.isNewUI) {
    return (
      <>
        {renderForgotPasswordModal()}
        {(!isVerifiedEmail || !props.isEmailVerificationActive) && (
          <>
            <Text allowFontScaling={false} style={st.headerTitle}>
              {props?.header?.title}
            </Text>
            <Text allowFontScaling={false} style={st.headerDescription}>
              {props?.header?.description}
            </Text>
          </>
        )}
        {renderPasswordResetSuccessBanner()}
        <View style={st.mainContainer}>
          {props.isEmailVerificationActive && isVerifiedEmail ? (
            <>
              <EmailCodeInput
                key={`email-code-input-${email}`}
                colorVariant={'dark'}
                numberOfInputs={5}
                email={email}
                editPress={() => {
                  setIsVerifiedEmail(false);
                  setOtpError('');
                }}
                disableEdit={false}
                isNewUI={props?.isNewUI}
                onCodeComplete={handleOtpVerification}
                // onSignUpWithPassword={props?.isVerifiedEmail}
                fieldType={props?.fieldType}
                time={props?.time}
                forgotPasswordCallback={() => setModalVisible(true)}
                handlePassword={props?.handlePassword}
                gameTheme={props.gameTheme}
                showCheckbox={true}
                checkboxText={'I consent to updates and offers from Skillz and partners.'}
                onOtpMethodSelected={props?.onOtpMethodSelected}
                isSignUp={props?.isSignUp}
                onCheckboxToggle={() => {}}
                codeValidation={props.codeValidation}
              />
            </>
          ) : (
            <>
              <TextInput
                ref={inputRef}
                placeholder={props?.isSignUp ? 'Enter email' : 'Enter email or username'}
                cursorColor={playButtonGradient?.[1]}
                value={email}
                onChangeText={(value) => {
                  setEmail(value);
                  props?.setEmailUsername?.(value);
                }}
                autoCorrect={false}
                textContentType='emailAddress'
                placeholderTextColor={
                  isDarkTheme ? textColorPrimary?.color : textColorSecondary?.color
                }
                style={st.textInput}
                keyboardType={'email-address'}
                autoCapitalize={'none'}
                testID={TEST_IDS.inputUsernameTestID}
                allowFontScaling={false}
              />
              {renderNewUINextButton()}
            </>
          )}
          {renderPasswordResetSuccessToast()}
          {renderTOS()}
        </View>
      </>
    );
  }

  return (
    <View style={styles.container} testID={TEST_IDS.modalTestID}>
      {renderForgotPasswordModal()}
      <ScrollView
        style={styles.containerScrollview}
        keyboardShouldPersistTaps={'always'}
        showsVerticalScrollIndicator={false}
        horizontal={false}
      >
        <KeyboardAvoidingView behavior='position' testID={TEST_IDS.modalTestID}>
          {renderPasswordResetSuccessBanner()}
          {renderContainerView()}
          <TouchableOpacity
            onPress={() => {
              trackTelemetryEvent({
                event: TelemetryEventName.SIGN_IN_CTA_CLICK,
                payload: {cta: 'forgot your password'},
              });
              setModalVisible(true);
            }}
            testID={TEST_IDS.forgotPasswordTestID}
          >
            <Text allowFontScaling={false} style={styles.forgotBtn}>
              {_m('Forgot your password?')}
            </Text>
          </TouchableOpacity>
        </KeyboardAvoidingView>
      </ScrollView>
      {renderPasswordResetSuccessToast()}
      {renderTOS()}
    </View>
  );
};

const createStyles = (gameTheme: any, isDarkTheme: boolean) => {
  const {
    textColorPrimary,
    textColorTertiary,
    fontBold,
    fontRegular,
    fontSizeH4,
    fontSizeH1,
    fontSizeH5,
    btnGradientOne,
    btnTextColorOne,
    viewPrimaryColor,
    textColorSecondary,
    viewSecondaryColor,
    playButtonGradient,
  } = gameTheme ?? {};
  return StyleSheet.create({
    headerTitle: {
      ...fontRegular,
      ...fontBold,
      ...fontSizeH1,
      ...textColorPrimary,
      textAlign: 'center',
    },
    headerDescription: {
      ...fontRegular,
      ...fontSizeH5,
      ...textColorTertiary,
      textAlign: 'center',
      marginTop: 8,
    },
    modalContainer: {
      justifyContent: 'flex-start',
      paddingTop: 20,
    },
    nextButtonContainer: {
      marginTop: 16,
    },
    buttonTextStyle: {
      color: btnTextColorOne?.color,
      fontSize: 18,
      ...SkillzStyle.fullStyles.fontBold,
    },
    mainContainer: {
      marginTop: 12,
    },
    errorMessage: {
      color: '#FF3B30',
      fontSize: 14,
      textAlign: 'center',
      marginTop: 12,
      ...fontRegular,
    },
    textInput: {
      paddingHorizontal: 20,
      paddingVertical: 7,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: playButtonGradient?.[0],
      color: isDarkTheme ? textColorPrimary?.color : textColorSecondary?.color,
      height: 56,
      backgroundColor: isDarkTheme ? 'rgba(0, 0, 0, 0.25)' : viewSecondaryColor?.backgroundColor,
      opacity: 0.86,
    },
  });
};

export default EmailSignInView;
